/** Onglets pièces du client — devis, factures, récurrences, paiements. */
import { useEffect, useState } from "react";
import { facturxFetch } from "../../api/client";
import { Button } from "../../components/Button";
import { ListPager, usePagedRows } from "../../components/ListPager";
import { SortTh, sortRows, useTableSort, type SortKind } from "../../components/tableSort";
import { useFormatters } from "../../formats/useFormatters";
import { StatusChip } from "../../status/StatusChip";
import { quoteStatus } from "../quotes/format";
import { quoteValidUntil, type Quote } from "../quotes/types";
import { PaymentLineActions } from "../payments/PaymentLineActions";
import { asPayment, invoiceRemainingCents, invoiceSoldeText, invoiceSoldeTone, payMethodLabel, payStatusClass, payStatusLabel, type Payment } from "../payments/types";
import { useCompany } from "../../company/CompanyProvider";
import { formatsFromCompany } from "../../formats/types";
import { recDueDays, recDueOn, recFreqLabel, recStatusLabel, recStatusTone, type Recurring } from "../recurring/types";
import { ClientPortalPanel } from "./ClientPortalPanel";
import type { Client } from "./types";
import "./tables.css";

export type PieceTab = "quotes" | "invoices" | "recurring" | "payments" | "portal";

const TABS: { id: PieceTab; label: string }[] = [
  { id: "quotes", label: "Devis" },
  { id: "invoices", label: "Factures" },
  { id: "recurring", label: "Factures récurrentes" },
  { id: "payments", label: "Paiements" },
  { id: "portal", label: "Portail client" },
];

type PieceCol = { label: string; key: string; kind: SortKind; num?: boolean };

const COLS: Record<Exclude<PieceTab, "portal">, PieceCol[]> = {
  invoices: [
    { label: "Statut", key: "status", kind: "text" },
    { label: "N°", key: "number", kind: "text" },
    { label: "Date", key: "date", kind: "date" },
    { label: "Échéance", key: "due", kind: "date" },
    { label: "TTC", key: "ttc", kind: "num" },
    { label: "Solde", key: "balance", kind: "num", num: true },
  ],
  quotes: [
    { label: "Statut", key: "status", kind: "text" },
    { label: "N°", key: "number", kind: "text" },
    { label: "Date", key: "date", kind: "date" },
    { label: "Validité", key: "due", kind: "date" },
    { label: "TTC", key: "ttc", kind: "num" },
  ],
  payments: [
    { label: "Statut", key: "status", kind: "text" },
    { label: "N°", key: "number", kind: "text" },
    { label: "Date", key: "date", kind: "date" },
    { label: "Facture", key: "invoice", kind: "text" },
    { label: "Méthode", key: "method", kind: "text" },
    { label: "Montant", key: "amount", kind: "num", num: true },
    { label: "Remboursé", key: "refund", kind: "num", num: true },
    { label: "", key: "act", kind: "text" },
  ],
  recurring: [
    { label: "Statut", key: "status", kind: "text" },
    { label: "N°", key: "number", kind: "text" },
    { label: "Fréquence", key: "freq", kind: "text" },
    { label: "Début", key: "start", kind: "date" },
    { label: "Prochaine", key: "next", kind: "date" },
    { label: "Échéance", key: "due", kind: "date" },
    { label: "TTC", key: "ttc", kind: "num" },
  ],
};

const EMPTY: Record<Exclude<PieceTab, "portal">, string> = {
  invoices: "Aucune facture pour ce client.",
  quotes: "Aucun devis pour ce client.",
  payments: "Aucun paiement pour ce client.",
  recurring: "Aucune facture récurrente pour ce client.",
};

type ClientPieceTabsProps = {
  client: Client;
  onNewQuote?: () => void;
  onOpenQuote?: (id: number) => void;
  onNewInvoice?: () => void;
  onOpenInvoice?: (id: number) => void;
  onNewRecurring?: () => void;
  onOpenRecurring?: (id: number) => void;
  /** Après un encaissement : le solde client change. */
  onLedgerChange?: () => void;
};

export function ClientPieceTabs({ client, onNewQuote, onOpenQuote, onNewInvoice, onOpenInvoice, onNewRecurring, onOpenRecurring, onLedgerChange }: ClientPieceTabsProps) {
  const { company } = useCompany();
  const { money, day } = useFormatters();
  const decimalComma = formatsFromCompany(company).decimal_comma;
  const [tab, setTab] = useState<PieceTab>("quotes");
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [invoices, setInvoices] = useState<Quote[]>([]);
  const [recurring, setRecurring] = useState<Recurring[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState("");
  const [payNotice, setPayNotice] = useState("");
  const [payMenuId, setPayMenuId] = useState<number | null>(null);
  const sort = useTableSort<string>();
  const cols = tab === "portal" ? [] : COLS[tab];

  function ordered<T>(which: Exclude<PieceTab, "portal">, list: T[], pick: (row: T, key: string) => unknown): T[] {
    const col = COLS[which].find((c) => c.label && sort.key === `${which}:${c.key}`);
    if (!col) return list;
    return sortRows(list, sort.dir, col.kind, (row) => pick(row, col.key));
  }

  function head(which: Exclude<PieceTab, "portal">) {
    return COLS[which].map((c) =>
      c.label ? (
        <SortTh
          key={c.key}
          label={c.label}
          className={c.num ? "is-num" : undefined}
          active={sort.key === `${which}:${c.key}`}
          dir={sort.dir}
          onSort={() => sort.onSort(`${which}:${c.key}`, c.kind)}
        />
      ) : (
        <th key={c.key} className="is-act" scope="col">
          <span className="sr-only">Actions</span>
        </th>
      ),
    );
  }

  const quoteRows = ordered("quotes", quotes, (q, key) => {
    if (key === "status") return quoteStatus(q);
    if (key === "number") return q.number || "";
    if (key === "date") return q.issue_date || "";
    if (key === "due") return quoteValidUntil(q) || "";
    return q.total_ttc_cents;
  });
  const invoiceRows = ordered("invoices", invoices, (inv, key) => {
    if (key === "status") return quoteStatus({ ...inv, doc_type: "invoice" });
    if (key === "number") return inv.number || "";
    if (key === "date") return inv.issue_date || "";
    if (key === "due") return inv.due_date || "";
    if (key === "ttc") return inv.total_ttc_cents;
    return invoiceRemainingCents(inv);
  });
  const recurringRows = ordered("recurring", recurring, (r, key) => {
    if (key === "status") return recStatusLabel(r.status, r.waiting);
    if (key === "number") return r.number || "";
    if (key === "freq") return recFreqLabel(r.frequency);
    if (key === "start") return r.starts_on || "";
    if (key === "next") return r.next_run_on || "";
    if (key === "due") return recDueDays(r.due_days) === 0 ? "" : recDueOn(r) || "";
    return r.total_ttc_cents;
  });
  const paymentRows = ordered("payments", payments, (p, key) => {
    if (key === "status") return payStatusLabel(p);
    if (key === "number") return p.number || "";
    if (key === "date") return p.paid_at || "";
    if (key === "invoice") return p.document_number || "";
    if (key === "method") return payMethodLabel(p);
    if (key === "amount") return p.amount_cents;
    return p.refunded_cents > 0 ? p.refunded_cents : null;
  });
  const quotePage = usePagedRows(quoteRows);
  const invoicePage = usePagedRows(invoiceRows);
  const recurringPage = usePagedRows(recurringRows);
  const paymentPage = usePagedRows(paymentRows);

  useEffect(() => {
    if (tab === "payments") {
      facturxFetch<{ payments: Payment[] }>(`payments.php?company_id=${client.company_id}&client_id=${client.id}`)
        .then((data) => setPayments((data.payments || []).map(asPayment)))
        .catch(() => setPayments([]));
      return;
    }
    if (tab === "recurring") {
      facturxFetch<{ recurring: Recurring[] }>(`recurring.php?company_id=${client.company_id}&client_id=${client.id}`)
        .then((data) => setRecurring(data.recurring || []))
        .catch(() => setRecurring([]));
      return;
    }
    if (tab !== "quotes" && tab !== "invoices") return;
    const type = tab === "invoices" ? "invoice" : "quote";
    facturxFetch<{ documents: Quote[] }>(
      `documents.php?company_id=${client.company_id}&doc_type=${type}&client_id=${client.id}`
    )
      .then((data) => {
        const rows = data.documents || [];
        if (type === "invoice") setInvoices(rows);
        else setQuotes(rows);
      })
      .catch(() => {
        if (type === "invoice") setInvoices([]);
        else setQuotes([]);
      });
  }, [tab, client.id, client.company_id]);

  return (
    <section>
      <div className="fx-fiche-tabs" role="tablist" aria-label="Pièces du client">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? "is-on" : ""}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="fx-fiche-panel" role="tabpanel">
        {tab === "portal" ? (
          <ClientPortalPanel client={client} />
        ) : tab === "invoices" ? (
          <div>
            {onNewInvoice ? (
              <div className="mb-3">
                <Button onClick={onNewInvoice}>Nouvelle facture</Button>
              </div>
            ) : null}
            <table className="fx-data-table fx-fiche-table text-left text-sm">
              <thead>
                <tr>
                  {head("invoices")}
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td className="fx-fiche-empty" colSpan={cols.length}>
                      {EMPTY.invoices}
                    </td>
                  </tr>
                ) : (
                  invoicePage.slice.map((inv) => (
                    <tr
                      key={inv.id}
                      className={onOpenInvoice ? "fx-clients-table__row" : undefined}
                      onClick={onOpenInvoice ? () => onOpenInvoice(inv.id) : undefined}
                    >
                      <td>
                        <StatusChip row={{ ...inv, doc_type: "invoice" }} />
                      </td>
                      <td>{inv.number || "—"}</td>
                      <td>{day(inv.issue_date)}</td>
                      <td>{day(inv.due_date)}</td>
                      <td>{money(inv.total_ttc_cents)}</td>
                      <td className={invoiceSoldeTone(invoiceRemainingCents(inv))}>
                        {invoiceSoldeText(invoiceRemainingCents(inv), money)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <ListPager page={invoicePage.page} pages={invoicePage.pages} total={invoicePage.total} from={invoicePage.from} to={invoicePage.to} setPage={invoicePage.setPage} />
          </div>
        ) : tab === "quotes" ? (
          <div>
            {onNewQuote ? (
              <div className="mb-3">
                <Button onClick={onNewQuote}>Nouveau devis</Button>
              </div>
            ) : null}
            <table className="fx-data-table fx-fiche-table text-left text-sm">
              <thead>
                <tr>
                  {head("quotes")}
                </tr>
              </thead>
              <tbody>
                {quotes.length === 0 ? (
                  <tr>
                    <td className="fx-fiche-empty" colSpan={cols.length}>
                      {EMPTY.quotes}
                    </td>
                  </tr>
                ) : (
                  quotePage.slice.map((q) => (
                    <tr
                      key={q.id}
                      className={onOpenQuote ? "fx-clients-table__row" : undefined}
                      onClick={onOpenQuote ? () => onOpenQuote(q.id) : undefined}
                    >
                      <td>
                        <StatusChip row={q} />
                      </td>
                      <td>{q.number || "—"}</td>
                      <td>{day(q.issue_date)}</td>
                      <td>{day(quoteValidUntil(q))}</td>
                      <td>{money(q.total_ttc_cents)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <ListPager page={quotePage.page} pages={quotePage.pages} total={quotePage.total} from={quotePage.from} to={quotePage.to} setPage={quotePage.setPage} />
          </div>
        ) : tab === "recurring" ? (
          <div>
            {onNewRecurring ? (
              <div className="mb-3">
                <Button onClick={onNewRecurring}>Nouvelle facture récurrente</Button>
              </div>
            ) : null}
            <table className="fx-data-table fx-fiche-table text-left text-sm">
              <thead>
                <tr>
                  {head("recurring")}
                </tr>
              </thead>
              <tbody>
                {recurring.length === 0 ? (
                  <tr>
                    <td className="fx-fiche-empty" colSpan={cols.length}>
                      {EMPTY.recurring}
                    </td>
                  </tr>
                ) : (
                  recurringPage.slice.map((r) => (
                    <tr
                      key={r.id}
                      className={onOpenRecurring ? "fx-clients-table__row" : undefined}
                      onClick={onOpenRecurring ? () => onOpenRecurring(r.id) : undefined}
                    >
                      <td>
                        <span className={`fx-status is-${recStatusTone(r.status, r.waiting)}`}>{recStatusLabel(r.status, r.waiting)}</span>
                      </td>
                      <td>
                        {onOpenRecurring ? (
                          <button
                            type="button"
                            className="fx-clients-table__name fx-clients-table__link"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenRecurring(r.id);
                            }}
                          >
                            {r.number || "—"}
                          </button>
                        ) : (
                          r.number || "—"
                        )}
                      </td>
                      <td>{recFreqLabel(r.frequency)}</td>
                      <td>{day(r.starts_on)}</td>
                      <td>{day(r.next_run_on)}</td>
                      <td>{recDueDays(r.due_days) === 0 ? "À réception" : day(recDueOn(r))}</td>
                      <td>{money(r.total_ttc_cents)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <ListPager page={recurringPage.page} pages={recurringPage.pages} total={recurringPage.total} from={recurringPage.from} to={recurringPage.to} setPage={recurringPage.setPage} />
          </div>
        ) : tab === "payments" ? (
          <div>
            {payError ? <p className="mb-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">{payError}</p> : null}
            {payNotice ? <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{payNotice}</p> : null}
          <table className="fx-data-table fx-fiche-table text-left text-sm">
            <thead>
              <tr>
                {head("payments")}
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td className="fx-fiche-empty" colSpan={cols.length}>
                    {EMPTY.payments}
                  </td>
                </tr>
              ) : (
                paymentPage.slice.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span className={`fx-status ${payStatusClass(p.status)}`}>{payStatusLabel(p)}</span>
                    </td>
                    <td>
                      <span className="fx-clients-table__name">{p.number || "—"}</span>
                    </td>
                    <td>{day(p.paid_at)}</td>
                    <td>
                      {onOpenInvoice ? (
                        <button type="button" className="fx-clients-table__link" onClick={() => onOpenInvoice(p.document_id)}>
                          {p.document_number || "—"}
                        </button>
                      ) : (
                        p.document_number || "—"
                      )}
                    </td>
                    <td>{payMethodLabel(p)}</td>
                    <td>{money(p.amount_cents)}</td>
                    <td>{p.refunded_cents > 0 ? money(p.refunded_cents) : "—"}</td>
                    <td className="is-act">
                      <PaymentLineActions
                        row={p}
                        decimalComma={decimalComma}
                        busy={payBusy}
                        open={payMenuId === p.id}
                        onOpenChange={(next) => setPayMenuId(next ? p.id : null)}
                        onBusy={setPayBusy}
                        onError={setPayError}
                        onNotice={setPayNotice}
                        onChanged={(next) => {
                          setPayments((list) => list.map((row) => (row.id === next.id ? next : row)));
                          onLedgerChange?.();
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <ListPager page={paymentPage.page} pages={paymentPage.pages} total={paymentPage.total} from={paymentPage.from} to={paymentPage.to} setPage={paymentPage.setPage} />
          </div>
        ) : (
          <table className="fx-data-table fx-fiche-table text-left text-sm">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="fx-fiche-empty" colSpan={cols.length}>
                  {EMPTY[tab]}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
